import * as React from 'react';
import { useState } from 'react';

import { useMutation } from '@apollo/client';
import UpdateReportProjectName from '@graphql/mutations/UpdateReportProjectName';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import MaterialButton from 'components/MaterialButton';
import { useTranslation } from 'react-i18next';

interface Props {
  report: reports.AllReportsQueryRow;
  onClose: () => void;
  onSaved: () => void;
}

const EditReportProjectNameModal: React.FC<Props> = ({
  report,
  onClose,
  onSaved,
}) => {
  const { t } = useTranslation();
  const [projectName, setProjectName] = useState(report.projectName ?? '');
  const [updateProjectName, { loading: saving }] = useMutation(
    UpdateReportProjectName,
  );

  const trimmed = projectName.trim();
  const canSave = !!trimmed && trimmed !== report.projectName && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    await updateProjectName({
      variables: { jobId: report.jobId, projectName: trimmed },
    });
    onSaved();
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('reportsQa.edit.projectNameTitle')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {t('reportsQa.edit.reportId', { reportId: report.reportId })}
        </Typography>
        <TextField
          id="enter-report-new-project-name"
          autoFocus
          fullWidth
          margin="dense"
          label={t('panel.projectName')}
          value={projectName}
          onChange={(event) => setProjectName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') handleSave();
          }}
        />
      </DialogContent>
      <DialogActions>
        <MaterialButton variant="outlined" soft onClick={onClose}>
          {t('general.cancel')}
        </MaterialButton>
        <MaterialButton
          variant="outlined"
          soft
          teal
          disabled={!canSave}
          onClick={handleSave}
        >
          {t('reportsQa.edit.save')}
        </MaterialButton>
      </DialogActions>
    </Dialog>
  );
};

export default EditReportProjectNameModal;
